import { useCallback, useEffect, useMemo, useState } from "react";
import Button from "@mui/material/Button";
import TextField from "@mui/material/TextField";
import Page from "../Components/Page";
import { axiosInstance } from "../Components/axiosInstance.js";

const DEFAULT_FORM = {
  role: "PARTNER",
  email: "",
  first_name: "",
  last_name: "",
  display_name: "",
  partner_name: "",
  partner_username: "",
  send_email: true,
};

const DEFAULT_DELEGATION_FORM = {
  aggregator_email: "",
  grower_email: "",
  grower_profile_public_id: "",
  scope: ["data.read", "data.write", "import.write", "reconciliation.write"],
  notes: "",
};

const DEFAULT_PARTNER_MEMBER_FORM = {
  partner_id: "",
  email: "",
};

const DELEGATION_SCOPE_OPTIONS = [
  {
    value: "data.read",
    label: "View grower records",
    description: "Dashboard, farms, fields, crop years, libraries, analysis, and Holos status.",
  },
  {
    value: "data.write",
    label: "Update grower records",
    description: "Create, edit, delete, calculate analysis, and submit Holos jobs.",
  },
  {
    value: "import.write",
    label: "Run imports",
    description: "Preview and apply CSV or Excel imports for the selected grower.",
  },
  {
    value: "reconciliation.write",
    label: "Resolve conflicts",
    description: "Review and apply reconciliation decisions from import conflicts.",
  },
];

const WORKFORCE_ROLE_OPTIONS = [
  { value: "ALL", label: "All workforce" },
  { value: "PARTNER", label: "Partners" },
  { value: "AGGREGATOR", label: "Aggregators" },
  { value: "ADMIN", label: "Admins" },
];

const AUDIT_EVENT_OPTIONS = [
  { value: "", label: "All events" },
  { value: "admin.workforce_user", label: "Workforce users" },
  { value: "admin.partner_workspace", label: "Partner memberships" },
  { value: "delegation.admin", label: "Admin grower links" },
];

const HEALTH_STATUS_STYLE = {
  GRAPH_UNAVAILABLE: "border-[#d8a31a] bg-[#fff8e1] text-[#8a5b00]",
  HEALTHY: "border-[#a7d7b7] bg-[#eef8f0] text-[#1f6d38]",
  MISSING_GRAPH_USER: "border-[#f2b8b5] bg-[#fff1f0] text-[#9b1c13]",
  MISSING_GROUP_MEMBERSHIP: "border-[#f2b8b5] bg-[#fff1f0] text-[#9b1c13]",
  UNSUPPORTED_ROLE: "border-slate-200 bg-[#f8fafc] text-[#667386]",
};

function fieldValue(value) {
  return String(value || "").trim() || null;
}

function unwrapRows(payload) {
  if (Array.isArray(payload?.items)) {
    return payload.items;
  }
  if (Array.isArray(payload?.data)) {
    return payload.data;
  }
  if (Array.isArray(payload)) {
    return payload;
  }
  return [];
}

function formatScopeLabel(scope) {
  const match = DELEGATION_SCOPE_OPTIONS.find((option) => option.value === scope);
  if (match) {
    return match.label;
  }
  if (scope === "all") {
    return "All permissions";
  }
  return String(scope || "").replace(".", " ");
}

function formatDate(value) {
  if (!value) {
    return "Never";
  }
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return "Unknown";
  }
  return date.toLocaleDateString(undefined, {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}

function displayName(user) {
  const name = [user?.first_name, user?.last_name].filter(Boolean).join(" ");
  return name || user?.email || "Workforce user";
}

function formatAuditEvent(value) {
  return String(value || "")
    .replace(/^admin\./, "")
    .replaceAll("_", " ")
    .replaceAll(".", " / ");
}

function auditDetailSummary(detail) {
  if (!detail || typeof detail !== "object") {
    return "";
  }
  const fields = [
    detail.email,
    detail.role,
    detail.aggregator_email,
    detail.group_id,
    detail.partner_public_id,
    detail.previous_partner_public_id,
  ].filter(Boolean);
  return fields.length ? fields.join(" - ") : JSON.stringify(detail);
}

function formatHealthStatus(status) {
  return String(status || "Not checked")
    .replaceAll("_", " ")
    .toLowerCase()
    .replace(/\b\w/g, (letter) => letter.toUpperCase());
}

export default function AdminWorkforceProvisioningPage() {
  const [form, setForm] = useState(DEFAULT_FORM);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [result, setResult] = useState(null);
  const [errorMessage, setErrorMessage] = useState("");
  const [delegationForm, setDelegationForm] = useState(DEFAULT_DELEGATION_FORM);
  const [delegations, setDelegations] = useState([]);
  const [delegationMessage, setDelegationMessage] = useState("");
  const [delegationError, setDelegationError] = useState("");
  const [isDelegationSubmitting, setIsDelegationSubmitting] = useState(false);
  const [workforceUsers, setWorkforceUsers] = useState([]);
  const [usersLoading, setUsersLoading] = useState(false);
  const [usersError, setUsersError] = useState("");
  const [userFilters, setUserFilters] = useState({
    role: "ALL",
    email: "",
  });
  const [inviteActionUserId, setInviteActionUserId] = useState(null);
  const [inviteActionMessage, setInviteActionMessage] = useState("");
  const [inviteActionError, setInviteActionError] = useState("");
  const [healthByUserId, setHealthByUserId] = useState({});
  const [healthActionUserId, setHealthActionUserId] = useState(null);
  const [healthError, setHealthError] = useState("");
  const [partnerWorkspaces, setPartnerWorkspaces] = useState([]);
  const [partnerWorkspacesLoading, setPartnerWorkspacesLoading] = useState(false);
  const [partnerWorkspacesError, setPartnerWorkspacesError] = useState("");
  const [partnerWorkspaceSearch, setPartnerWorkspaceSearch] = useState("");
  const [partnerMemberForm, setPartnerMemberForm] = useState(DEFAULT_PARTNER_MEMBER_FORM);
  const [partnerMemberMessage, setPartnerMemberMessage] = useState("");
  const [partnerMemberError, setPartnerMemberError] = useState("");
  const [partnerMemberActionKey, setPartnerMemberActionKey] = useState("");
  const [auditEvents, setAuditEvents] = useState([]);
  const [auditLoading, setAuditLoading] = useState(false);
  const [auditError, setAuditError] = useState("");
  const [auditFilters, setAuditFilters] = useState({
    event_type: "",
    actor_email: "",
  });

  const isPartner = form.role === "PARTNER";
  const canSubmit = form.email.trim() && form.role && !isSubmitting;
  const canSubmitPartnerMember =
    partnerMemberForm.partner_id &&
    partnerMemberForm.email.trim() &&
    !partnerMemberActionKey;
  const canSubmitDelegation =
    delegationForm.aggregator_email.trim() &&
    (delegationForm.grower_email.trim() ||
      delegationForm.grower_profile_public_id.trim()) &&
    delegationForm.scope.length > 0 &&
    !isDelegationSubmitting;

  const loadDelegations = useCallback(async () => {
    try {
      const response = await axiosInstance.get("/delegations/admin");
      setDelegations(unwrapRows(response.data));
    } catch (error) {
      setDelegations([]);
    }
  }, []);

  const loadWorkforceUsers = useCallback(async () => {
    setUsersLoading(true);
    setUsersError("");
    try {
      const response = await axiosInstance.get("/admin/platform-users", {
        params: {
          page: 1,
          page_size: 100,
          workforce_only: userFilters.role === "ALL",
          ...(userFilters.role !== "ALL" ? { account_type: userFilters.role } : {}),
          ...(userFilters.email.trim() ? { email: userFilters.email.trim() } : {}),
        },
      });
      setWorkforceUsers(unwrapRows(response.data));
    } catch (error) {
      setWorkforceUsers([]);
      setUsersError("Workforce users could not be loaded.");
    } finally {
      setUsersLoading(false);
    }
  }, [userFilters]);

  const loadPartnerWorkspaces = useCallback(async () => {
    setPartnerWorkspacesLoading(true);
    setPartnerWorkspacesError("");
    try {
      const response = await axiosInstance.get("/admin/partner-workspaces", {
        params: {
          page: 1,
          page_size: 100,
          ...(partnerWorkspaceSearch.trim()
            ? { search: partnerWorkspaceSearch.trim() }
            : {}),
        },
      });
      setPartnerWorkspaces(unwrapRows(response.data));
    } catch (error) {
      setPartnerWorkspaces([]);
      setPartnerWorkspacesError("Partner workspaces could not be loaded.");
    } finally {
      setPartnerWorkspacesLoading(false);
    }
  }, [partnerWorkspaceSearch]);

  const loadAuditEvents = useCallback(async () => {
    setAuditLoading(true);
    setAuditError("");
    try {
      const response = await axiosInstance.get("/admin/audit-events", {
        params: {
          page: 1,
          page_size: 25,
          workforce_only: true,
          ...(auditFilters.event_type
            ? { event_type: auditFilters.event_type }
            : {}),
          ...(auditFilters.actor_email.trim()
            ? { actor_email: auditFilters.actor_email.trim() }
            : {}),
        },
      });
      setAuditEvents(unwrapRows(response.data));
    } catch (error) {
      setAuditEvents([]);
      setAuditError("Audit activity could not be loaded.");
    } finally {
      setAuditLoading(false);
    }
  }, [auditFilters]);

  useEffect(() => {
    loadDelegations();
    loadWorkforceUsers();
    loadPartnerWorkspaces();
    loadAuditEvents();
  }, [loadDelegations, loadWorkforceUsers, loadPartnerWorkspaces, loadAuditEvents]);

  const workforceCounts = useMemo(() => {
    return workforceUsers.reduce(
      (counts, user) => {
        const role = user.account_type || "UNKNOWN";
        return {
          ...counts,
          total: counts.total + 1,
          [role]: (counts[role] || 0) + 1,
        };
      },
      { total: 0, PARTNER: 0, AGGREGATOR: 0, ADMIN: 0 }
    );
  }, [workforceUsers]);

  function setField(field, value) {
    setForm((current) => ({ ...current, [field]: value }));
  }

  function setDelegationField(field, value) {
    setDelegationForm((current) => ({ ...current, [field]: value }));
  }

  function setPartnerMemberField(field, value) {
    setPartnerMemberForm((current) => ({ ...current, [field]: value }));
  }

  function setAuditFilter(field, value) {
    setAuditFilters((current) => ({ ...current, [field]: value }));
  }

  function toggleDelegationScope(scope) {
    setDelegationForm((current) => {
      const selected = current.scope.includes(scope)
        ? current.scope.filter((item) => item !== scope)
        : [...current.scope, scope];

      return { ...current, scope: selected };
    });
  }

  async function handleSubmit(event) {
    event.preventDefault();
    if (!canSubmit) {
      return;
    }
    setIsSubmitting(true);
    setErrorMessage("");
    setResult(null);
    try {
      const response = await axiosInstance.post("/admin/workforce-users", {
        role: form.role,
        email: fieldValue(form.email),
        first_name: fieldValue(form.first_name),
        last_name: fieldValue(form.last_name),
        display_name: fieldValue(form.display_name),
        partner_name: isPartner ? fieldValue(form.partner_name) : null,
        partner_username: isPartner ? fieldValue(form.partner_username) : null,
        send_email: form.send_email,
      });
      setResult(response.data);
      setForm(DEFAULT_FORM);
      await loadWorkforceUsers();
      await loadAuditEvents();
    } catch (error) {
      setErrorMessage(
        error?.response?.data?.detail ||
          "The workforce user could not be provisioned."
      );
    } finally {
      setIsSubmitting(false);
    }
  }

  async function handleDelegationSubmit(event) {
    event.preventDefault();
    if (!canSubmitDelegation) {
      return;
    }
    setIsDelegationSubmitting(true);
    setDelegationMessage("");
    setDelegationError("");
    try {
      const response = await axiosInstance.post("/delegations/admin", {
        aggregator_email: fieldValue(delegationForm.aggregator_email),
        grower_email: fieldValue(delegationForm.grower_email),
        grower_profile_public_id: fieldValue(
          delegationForm.grower_profile_public_id
        ),
        scope: delegationForm.scope,
        notes: fieldValue(delegationForm.notes),
      });
      setDelegationMessage(
        `${response.data?.aggregator_email} linked to ${
          response.data?.grower_display_name ||
          response.data?.grower_profile_public_id ||
          "grower"
        }.`
      );
      setDelegationForm(DEFAULT_DELEGATION_FORM);
      await loadDelegations();
      await loadAuditEvents();
    } catch (error) {
      setDelegationError(
        error?.response?.data?.detail || "The grower link could not be saved."
      );
    } finally {
      setIsDelegationSubmitting(false);
    }
  }

  async function revokeDelegation(publicId) {
    setDelegationMessage("");
    setDelegationError("");
    try {
      await axiosInstance.delete(`/delegations/admin/${publicId}`, {
        data: { reason: "Revoked from admin portal" },
      });
      setDelegationMessage("Grower link revoked.");
      await loadDelegations();
      await loadAuditEvents();
    } catch (error) {
      setDelegationError(
        error?.response?.data?.detail || "The grower link could not be revoked."
      );
    }
  }

  async function resendWorkforceInvite(user) {
    if (!user?.id || !["PARTNER", "AGGREGATOR"].includes(user.account_type)) {
      return;
    }
    setInviteActionUserId(user.id);
    setInviteActionMessage("");
    setInviteActionError("");
    try {
      const response = await axiosInstance.post(
        `/admin/workforce-users/${user.id}/resend-invite`,
        { send_email: true }
      );
      setInviteActionMessage(
        `${response.data?.user?.email || user.email} invite resent and group assignment checked.`
      );
      await loadWorkforceUsers();
      await loadAuditEvents();
    } catch (error) {
      setInviteActionError(
        error?.response?.data?.detail || "The workforce invite could not be resent."
      );
    } finally {
      setInviteActionUserId(null);
    }
  }

  async function inspectWorkforceHealth(user) {
    if (!user?.id || !["PARTNER", "AGGREGATOR"].includes(user.account_type)) {
      return;
    }
    setHealthActionUserId(user.id);
    setHealthError("");
    try {
      const response = await axiosInstance.get(
        `/admin/workforce-users/${user.id}/health`
      );
      setHealthByUserId((current) => ({
        ...current,
        [user.id]: response.data,
      }));
    } catch (error) {
      setHealthError(
        error?.response?.data?.detail || "Workforce health could not be checked."
      );
    } finally {
      setHealthActionUserId(null);
    }
  }

  async function handlePartnerMemberSubmit(event) {
    event.preventDefault();
    if (!canSubmitPartnerMember) {
      return;
    }
    setPartnerMemberActionKey("link");
    setPartnerMemberMessage("");
    setPartnerMemberError("");
    try {
      const response = await axiosInstance.post(
        `/admin/partner-workspaces/${partnerMemberForm.partner_id}/members`,
        { email: fieldValue(partnerMemberForm.email) }
      );
      const previous = response.data?.previous_partner;
      setPartnerMemberMessage(
        previous
          ? `${response.data?.email} moved from ${previous.name || previous.username}.`
          : `${response.data?.email} linked to ${
              response.data?.partner?.name || response.data?.partner?.username
            }.`
      );
      setPartnerMemberForm(DEFAULT_PARTNER_MEMBER_FORM);
      await loadPartnerWorkspaces();
      await loadWorkforceUsers();
      await loadAuditEvents();
    } catch (error) {
      setPartnerMemberError(
        error?.response?.data?.detail || "The partner member could not be linked."
      );
    } finally {
      setPartnerMemberActionKey("");
    }
  }

  async function unlinkPartnerMember(partnerId, email) {
    const actionKey = `${partnerId}:${email}`;
    setPartnerMemberActionKey(actionKey);
    setPartnerMemberMessage("");
    setPartnerMemberError("");
    try {
      await axiosInstance.delete(
        `/admin/partner-workspaces/${partnerId}/members/${encodeURIComponent(email)}`
      );
      setPartnerMemberMessage(`${email} unlinked from partner workspace.`);
      await loadPartnerWorkspaces();
      await loadWorkforceUsers();
      await loadAuditEvents();
    } catch (error) {
      setPartnerMemberError(
        error?.response?.data?.detail || "The partner member could not be unlinked."
      );
    } finally {
      setPartnerMemberActionKey("");
    }
  }

  return (
    <Page
      title="Workforce Provisioning"
      headerBorderColor="border-[#f15d22]"
      headerVariant="hybrid"
      contentWidth="wide"
      padding="p-4 md:p-6"
      introContent="Create internal-tenant Partner and Aggregator users, invite them as guests, assign the matching Entra group, and send a branded onboarding email."
    >
      <div className="flex w-full flex-col gap-5 text-[#5c6673]">
        {errorMessage ? (
          <div className="rounded-[6px] border border-[#f2b8b5] bg-[#fff1f0] px-4 py-3 text-[#9b1c13]">
            {errorMessage}
          </div>
        ) : null}

        {result ? (
          <div className="rounded-[8px] border border-[#a7d7b7] bg-[#eef8f0] p-5 text-[#1f6d38]">
            <p className="font-semibold">
              {result.user?.email} provisioned as {result.role}.
            </p>
            <p className="mt-1 text-sm">
              Group: {result.group_name} ({result.group_id})
            </p>
            <p className="mt-1 text-sm">
              Invite email: {result.email_sent ? "sent" : "not sent"}
            </p>
            {result.partner ? (
              <p className="mt-1 text-sm">
                Partner workspace: {result.partner.name || result.partner.username}
              </p>
            ) : null}
          </div>
        ) : null}

        <section className="rounded-[8px] border border-slate-200 bg-white p-5 shadow-[0_10px_24px_rgba(15,23,42,0.04)]">
          <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
            <div>
              <p className="text-sm font-semibold uppercase tracking-[0.14em] text-[#75839a]">
                Workforce Users
              </p>
              <p className="mt-1 text-sm text-[#5c6673]">
                Review internal-tenant Admin, Partner, and Aggregator accounts,
                their Entra group target, and any linked partner workspace.
              </p>
            </div>
            <div className="grid grid-cols-2 gap-3 text-sm sm:grid-cols-4">
              {[
                ["Total", workforceCounts.total],
                ["Partners", workforceCounts.PARTNER],
                ["Aggregators", workforceCounts.AGGREGATOR],
                ["Admins", workforceCounts.ADMIN],
              ].map(([label, value]) => (
                <div
                  className="min-w-[96px] rounded-[6px] border border-slate-200 bg-[#f8fafc] px-3 py-2"
                  key={label}
                >
                  <p className="text-xs uppercase tracking-[0.12em] text-[#75839a]">
                    {label}
                  </p>
                  <p className="mt-1 text-lg font-semibold text-[#243245]">
                    {value}
                  </p>
                </div>
              ))}
            </div>
          </div>

          <div className="mt-5 grid grid-cols-1 gap-3 md:grid-cols-[180px_minmax(0,1fr)_auto] md:items-center">
            <TextField
              label="Role"
              onChange={(event) =>
                setUserFilters((current) => ({
                  ...current,
                  role: event.target.value,
                }))
              }
              select
              InputLabelProps={{ shrink: true }}
              SelectProps={{ native: true }}
              size="small"
              value={userFilters.role}
            >
              {WORKFORCE_ROLE_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </TextField>
            <TextField
              label="Filter by email"
              onChange={(event) =>
                setUserFilters((current) => ({
                  ...current,
                  email: event.target.value,
                }))
              }
              size="small"
              type="email"
              value={userFilters.email}
            />
            <Button
              disabled={usersLoading}
              onClick={loadWorkforceUsers}
              variant="outlined"
            >
              Refresh
            </Button>
          </div>

          {usersError ? (
            <div className="mt-4 rounded-[6px] border border-[#f2b8b5] bg-[#fff1f0] px-4 py-3 text-[#9b1c13]">
              {usersError}
            </div>
          ) : null}
          {inviteActionError ? (
            <div className="mt-4 rounded-[6px] border border-[#f2b8b5] bg-[#fff1f0] px-4 py-3 text-[#9b1c13]">
              {inviteActionError}
            </div>
          ) : null}
          {inviteActionMessage ? (
            <div className="mt-4 rounded-[6px] border border-[#a7d7b7] bg-[#eef8f0] px-4 py-3 text-[#1f6d38]">
              {inviteActionMessage}
            </div>
          ) : null}
          {healthError ? (
            <div className="mt-4 rounded-[6px] border border-[#f2b8b5] bg-[#fff1f0] px-4 py-3 text-[#9b1c13]">
              {healthError}
            </div>
          ) : null}

          <div className="mt-5 overflow-hidden rounded-[8px] border border-slate-200">
            <div className="hidden grid-cols-[minmax(0,1.25fr)_100px_minmax(0,0.85fr)_minmax(0,0.85fr)_130px_minmax(0,1fr)_130px] gap-4 border-b border-slate-200 bg-[#f8fafc] px-4 py-3 text-xs font-semibold uppercase tracking-[0.12em] text-[#75839a] lg:grid">
              <span>User</span>
              <span>Role</span>
              <span>Group</span>
              <span>Partner Workspace</span>
              <span>Last Sign-In</span>
              <span>Health</span>
              <span>Actions</span>
            </div>
            {usersLoading ? (
              <div className="p-4 text-sm">Loading workforce users...</div>
            ) : workforceUsers.length === 0 ? (
              <div className="p-4 text-sm">No workforce users found.</div>
            ) : (
              <div className="divide-y divide-slate-200">
                {workforceUsers.map((user) => (
                  <div
                    className="grid gap-3 px-4 py-4 text-sm lg:grid-cols-[minmax(0,1.25fr)_100px_minmax(0,0.85fr)_minmax(0,0.85fr)_130px_minmax(0,1fr)_130px] lg:gap-4"
                    key={user.public_id || user.email}
                  >
                    <div>
                      <p className="font-semibold text-[#243245]">
                        {displayName(user)}
                      </p>
                      <p className="mt-1 break-all text-[#667386]">
                        {user.email}
                      </p>
                    </div>
                    <div>
                      <p className="lg:hidden text-xs font-semibold uppercase tracking-[0.12em] text-[#75839a]">
                        Role
                      </p>
                      <span className="inline-flex rounded-[999px] border border-[#d5dde7] bg-white px-2.5 py-1 text-xs font-semibold text-[#243245]">
                        {user.account_type}
                      </span>
                    </div>
                    <div>
                      <p className="lg:hidden text-xs font-semibold uppercase tracking-[0.12em] text-[#75839a]">
                        Group
                      </p>
                      <p className="font-medium text-[#243245]">
                        {user.workforce_group_name || "None"}
                      </p>
                      {user.workforce_group_id ? (
                        <p className="mt-1 break-all text-xs text-[#75839a]">
                          {user.workforce_group_id}
                        </p>
                      ) : null}
                    </div>
                    <div>
                      <p className="lg:hidden text-xs font-semibold uppercase tracking-[0.12em] text-[#75839a]">
                        Partner Workspace
                      </p>
                      <p className="font-medium text-[#243245]">
                        {user.partner?.name ||
                          user.partner?.username ||
                          (user.account_type === "PARTNER" ? "Not linked" : "Not applicable")}
                      </p>
                      {user.partner?.username ? (
                        <p className="mt-1 text-xs text-[#75839a]">
                          {user.partner.username}
                        </p>
                      ) : null}
                    </div>
                    <div>
                      <p className="lg:hidden text-xs font-semibold uppercase tracking-[0.12em] text-[#75839a]">
                        Last Sign-In
                      </p>
                      <p className="text-[#243245]">
                        {formatDate(user.last_sign_in)}
                      </p>
                      <p className="mt-1 text-xs text-[#75839a]">
                        Created {formatDate(user.created_at)}
                      </p>
                    </div>
                    <div>
                      <p className="lg:hidden text-xs font-semibold uppercase tracking-[0.12em] text-[#75839a]">
                        Health
                      </p>
                      {healthByUserId[user.id] ? (
                        <div>
                          <span
                            className={`inline-flex rounded-[999px] border px-2.5 py-1 text-xs font-semibold ${
                              HEALTH_STATUS_STYLE[healthByUserId[user.id].status] ||
                              "border-slate-200 bg-[#f8fafc] text-[#667386]"
                            }`}
                          >
                            {formatHealthStatus(healthByUserId[user.id].status)}
                          </span>
                          <p className="mt-1 text-xs leading-5 text-[#667386]">
                            {healthByUserId[user.id].message}
                          </p>
                        </div>
                      ) : (
                        <p className="text-sm text-[#667386]">Not checked</p>
                      )}
                    </div>
                    <div className="flex flex-col items-start gap-2">
                      <Button
                        aria-label={`Check health for ${user.email}`}
                        disabled={
                          healthActionUserId === user.id ||
                          !["PARTNER", "AGGREGATOR"].includes(user.account_type)
                        }
                        onClick={() => inspectWorkforceHealth(user)}
                        size="small"
                        variant="outlined"
                      >
                        {healthActionUserId === user.id ? "Checking..." : "Check Health"}
                      </Button>
                      <Button
                        aria-label={`Resend invite to ${user.email}`}
                        disabled={
                          inviteActionUserId === user.id ||
                          !["PARTNER", "AGGREGATOR"].includes(user.account_type)
                        }
                        onClick={() => resendWorkforceInvite(user)}
                        size="small"
                        variant="outlined"
                      >
                        {inviteActionUserId === user.id ? "Sending..." : "Resend Invite"}
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </section>

        <section className="rounded-[8px] border border-slate-200 bg-white p-5 shadow-[0_10px_24px_rgba(15,23,42,0.04)]">
          <div className="flex flex-col gap-1">
            <p className="text-sm font-semibold uppercase tracking-[0.14em] text-[#75839a]">
              Partner Workspace Memberships
            </p>
            <p className="text-sm text-[#5c6673]">
              Link provisioned Partner users to the workspace they should manage.
              Linking an email here reassigns it from any previous partner workspace.
            </p>
          </div>

          <div className="mt-5 grid grid-cols-1 gap-3 md:grid-cols-[minmax(0,1fr)_auto] md:items-center">
            <TextField
              label="Search workspaces"
              onChange={(event) => setPartnerWorkspaceSearch(event.target.value)}
              size="small"
              value={partnerWorkspaceSearch}
            />
            <Button
              disabled={partnerWorkspacesLoading}
              onClick={loadPartnerWorkspaces}
              variant="outlined"
            >
              Refresh
            </Button>
          </div>

          {partnerWorkspacesError ? (
            <div className="mt-4 rounded-[6px] border border-[#f2b8b5] bg-[#fff1f0] px-4 py-3 text-[#9b1c13]">
              {partnerWorkspacesError}
            </div>
          ) : null}
          {partnerMemberError ? (
            <div className="mt-4 rounded-[6px] border border-[#f2b8b5] bg-[#fff1f0] px-4 py-3 text-[#9b1c13]">
              {partnerMemberError}
            </div>
          ) : null}
          {partnerMemberMessage ? (
            <div className="mt-4 rounded-[6px] border border-[#a7d7b7] bg-[#eef8f0] px-4 py-3 text-[#1f6d38]">
              {partnerMemberMessage}
            </div>
          ) : null}

          <form
            className="mt-5 grid grid-cols-1 gap-4 md:grid-cols-[minmax(0,1fr)_minmax(0,1fr)_auto]"
            onSubmit={handlePartnerMemberSubmit}
          >
            <TextField
              label="Partner workspace"
              onChange={(event) =>
                setPartnerMemberField("partner_id", event.target.value)
              }
              required
              select
              InputLabelProps={{ shrink: true }}
              SelectProps={{ native: true }}
              value={partnerMemberForm.partner_id}
            >
              <option value="">Select workspace</option>
              {partnerWorkspaces.map((partner) => (
                <option key={partner.id} value={partner.id}>
                  {partner.name || partner.username}
                </option>
              ))}
            </TextField>
            <TextField
              label="Partner user email"
              onChange={(event) =>
                setPartnerMemberField("email", event.target.value)
              }
              required
              type="email"
              value={partnerMemberForm.email}
            />
            <div className="flex items-center justify-start md:justify-end">
              <Button
                disabled={!canSubmitPartnerMember}
                type="submit"
                variant="contained"
              >
                {partnerMemberActionKey === "link" ? "Linking..." : "Link Member"}
              </Button>
            </div>
          </form>

          <div className="mt-6 divide-y divide-slate-200 rounded-[8px] border border-slate-200">
            {partnerWorkspacesLoading ? (
              <div className="p-4 text-sm">Loading partner workspaces...</div>
            ) : partnerWorkspaces.length === 0 ? (
              <div className="p-4 text-sm">No partner workspaces found.</div>
            ) : (
              partnerWorkspaces.map((partner) => (
                <div
                  className="grid gap-4 p-4 lg:grid-cols-[minmax(0,0.9fr)_minmax(0,1.4fr)]"
                  key={partner.public_id || partner.id}
                >
                  <div>
                    <p className="font-semibold text-[#243245]">
                      {partner.name || partner.username}
                    </p>
                    <p className="mt-1 break-all text-sm text-[#667386]">
                      {partner.username}
                    </p>
                    <p className="mt-1 text-xs uppercase tracking-[0.12em] text-[#75839a]">
                      {partner.member_count || 0} members
                    </p>
                  </div>
                  <div className="flex flex-col gap-2">
                    {(partner.authorized_emails || []).length === 0 ? (
                      <p className="text-sm text-[#667386]">No linked members.</p>
                    ) : (
                      partner.authorized_emails.map((email) => (
                        <div
                          className="grid gap-3 rounded-[6px] border border-slate-200 bg-[#f8fafc] px-3 py-2 md:grid-cols-[minmax(0,1fr)_auto] md:items-center"
                          key={`${partner.id}:${email}`}
                        >
                          <span className="break-all text-sm text-[#243245]">
                            {email}
                          </span>
                          <Button
                            aria-label={`Unlink ${email} from ${
                              partner.name || partner.username
                            }`}
                            color="error"
                            disabled={partnerMemberActionKey === `${partner.id}:${email}`}
                            onClick={() => unlinkPartnerMember(partner.id, email)}
                            size="small"
                            variant="outlined"
                          >
                            {partnerMemberActionKey === `${partner.id}:${email}`
                              ? "Unlinking..."
                              : "Unlink"}
                          </Button>
                        </div>
                      ))
                    )}
                  </div>
                </div>
              ))
            )}
          </div>
        </section>

        <section className="rounded-[8px] border border-slate-200 bg-white p-5 shadow-[0_10px_24px_rgba(15,23,42,0.04)]">
          <div className="flex flex-col gap-1">
            <p className="text-sm font-semibold uppercase tracking-[0.14em] text-[#75839a]">
              Workforce Audit Activity
            </p>
            <p className="text-sm text-[#5c6673]">
              Review recent Admin provisioning, invite, partner membership, and
              grower-link changes.
            </p>
          </div>

          <div className="mt-5 grid grid-cols-1 gap-3 md:grid-cols-[220px_minmax(0,1fr)_auto] md:items-center">
            <TextField
              label="Event type"
              onChange={(event) => setAuditFilter("event_type", event.target.value)}
              select
              InputLabelProps={{ shrink: true }}
              SelectProps={{ native: true }}
              size="small"
              value={auditFilters.event_type}
            >
              {AUDIT_EVENT_OPTIONS.map((option) => (
                <option key={option.value || "all"} value={option.value}>
                  {option.label}
                </option>
              ))}
            </TextField>
            <TextField
              label="Filter by actor"
              onChange={(event) =>
                setAuditFilter("actor_email", event.target.value)
              }
              size="small"
              type="email"
              value={auditFilters.actor_email}
            />
            <Button
              disabled={auditLoading}
              onClick={loadAuditEvents}
              variant="outlined"
            >
              Refresh
            </Button>
          </div>

          {auditError ? (
            <div className="mt-4 rounded-[6px] border border-[#f2b8b5] bg-[#fff1f0] px-4 py-3 text-[#9b1c13]">
              {auditError}
            </div>
          ) : null}

          <div className="mt-5 overflow-hidden rounded-[8px] border border-slate-200">
            <div className="hidden grid-cols-[170px_minmax(0,1.1fr)_minmax(0,0.9fr)_minmax(0,1.2fr)] gap-4 border-b border-slate-200 bg-[#f8fafc] px-4 py-3 text-xs font-semibold uppercase tracking-[0.12em] text-[#75839a] lg:grid">
              <span>When</span>
              <span>Event</span>
              <span>Actor</span>
              <span>Resource / Detail</span>
            </div>
            {auditLoading ? (
              <div className="p-4 text-sm">Loading audit activity...</div>
            ) : auditEvents.length === 0 ? (
              <div className="p-4 text-sm">No workforce audit activity found.</div>
            ) : (
              <div className="divide-y divide-slate-200">
                {auditEvents.map((event) => (
                  <div
                    className="grid gap-3 px-4 py-4 text-sm lg:grid-cols-[170px_minmax(0,1.1fr)_minmax(0,0.9fr)_minmax(0,1.2fr)] lg:gap-4"
                    key={event.id}
                  >
                    <div>
                      <p className="lg:hidden text-xs font-semibold uppercase tracking-[0.12em] text-[#75839a]">
                        When
                      </p>
                      <p className="text-[#243245]">
                        {formatDate(event.created_at)}
                      </p>
                    </div>
                    <div>
                      <p className="lg:hidden text-xs font-semibold uppercase tracking-[0.12em] text-[#75839a]">
                        Event
                      </p>
                      <p className="font-semibold text-[#243245]">
                        {formatAuditEvent(event.event_type)}
                      </p>
                      <p className="mt-1 break-all text-xs text-[#75839a]">
                        {event.event_type}
                      </p>
                    </div>
                    <div>
                      <p className="lg:hidden text-xs font-semibold uppercase tracking-[0.12em] text-[#75839a]">
                        Actor
                      </p>
                      <p className="break-all text-[#243245]">
                        {event.actor_email || "System"}
                      </p>
                    </div>
                    <div>
                      <p className="lg:hidden text-xs font-semibold uppercase tracking-[0.12em] text-[#75839a]">
                        Resource / Detail
                      </p>
                      <p className="break-all font-medium text-[#243245]">
                        {[event.resource_type, event.resource_public_id]
                          .filter(Boolean)
                          .join(" / ") || "None"}
                      </p>
                      {auditDetailSummary(event.detail) ? (
                        <p className="mt-1 break-all text-xs text-[#75839a]">
                          {auditDetailSummary(event.detail)}
                        </p>
                      ) : null}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </section>

        <form
          className="rounded-[8px] border border-slate-200 bg-white p-5 shadow-[0_10px_24px_rgba(15,23,42,0.04)]"
          onSubmit={handleSubmit}
        >
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <TextField
              label="Role"
              onChange={(event) => setField("role", event.target.value)}
              select
              InputLabelProps={{ shrink: true }}
              SelectProps={{ native: true }}
              value={form.role}
            >
              <option value="PARTNER">Partner</option>
              <option value="AGGREGATOR">Aggregator</option>
            </TextField>
            <TextField
              label="Email"
              onChange={(event) => setField("email", event.target.value)}
              required
              type="email"
              value={form.email}
            />
            <TextField
              label="First name"
              onChange={(event) => setField("first_name", event.target.value)}
              value={form.first_name}
            />
            <TextField
              label="Last name"
              onChange={(event) => setField("last_name", event.target.value)}
              value={form.last_name}
            />
            <TextField
              label="Display name"
              onChange={(event) => setField("display_name", event.target.value)}
              value={form.display_name}
            />
            <label className="flex min-h-[56px] items-center gap-3 rounded-[4px] border border-[#cfd6df] px-3 text-sm font-semibold text-[#4d5968]">
              <input
                checked={form.send_email}
                onChange={(event) => setField("send_email", event.target.checked)}
                type="checkbox"
              />
              Send branded invite email
            </label>
          </div>

          {isPartner ? (
            <div className="mt-5 border-t border-slate-200 pt-5">
              <p className="text-sm font-semibold uppercase tracking-[0.14em] text-[#75839a]">
                Partner Workspace
              </p>
              <div className="mt-4 grid grid-cols-1 gap-4 md:grid-cols-2">
                <TextField
                  label="Partner name"
                  onChange={(event) => setField("partner_name", event.target.value)}
                  value={form.partner_name}
                />
                <TextField
                  label="Partner username"
                  onChange={(event) => setField("partner_username", event.target.value)}
                  value={form.partner_username}
                />
              </div>
            </div>
          ) : null}

          <div className="mt-6 flex justify-end">
            <Button disabled={!canSubmit} type="submit" variant="contained">
              {isSubmitting ? "Provisioning..." : "Provision User"}
            </Button>
          </div>
        </form>

        <section className="rounded-[8px] border border-slate-200 bg-white p-5 shadow-[0_10px_24px_rgba(15,23,42,0.04)]">
          <div className="flex flex-col gap-1">
            <p className="text-sm font-semibold uppercase tracking-[0.14em] text-[#75839a]">
              Aggregator Grower Links
            </p>
            <p className="text-sm text-[#5c6673]">
              Link provisioned Aggregator users to grower profiles. Aggregators
              can only act inside these active scopes.
            </p>
          </div>

          {delegationError ? (
            <div className="mt-4 rounded-[6px] border border-[#f2b8b5] bg-[#fff1f0] px-4 py-3 text-[#9b1c13]">
              {delegationError}
            </div>
          ) : null}
          {delegationMessage ? (
            <div className="mt-4 rounded-[6px] border border-[#a7d7b7] bg-[#eef8f0] px-4 py-3 text-[#1f6d38]">
              {delegationMessage}
            </div>
          ) : null}

          <form className="mt-5 grid grid-cols-1 gap-4 md:grid-cols-2" onSubmit={handleDelegationSubmit}>
            <TextField
              label="Aggregator email"
              onChange={(event) =>
                setDelegationField("aggregator_email", event.target.value)
              }
              required
              type="email"
              value={delegationForm.aggregator_email}
            />
            <TextField
              label="Grower email"
              onChange={(event) =>
                setDelegationField("grower_email", event.target.value)
              }
              type="email"
              value={delegationForm.grower_email}
            />
            <TextField
              label="Grower profile public ID"
              onChange={(event) =>
                setDelegationField("grower_profile_public_id", event.target.value)
              }
              value={delegationForm.grower_profile_public_id}
            />
            <div className="md:col-span-2">
              <p className="text-sm font-semibold uppercase tracking-[0.14em] text-[#75839a]">
                Delegation Scope
              </p>
              <div className="mt-3 grid gap-3 md:grid-cols-2">
                {DELEGATION_SCOPE_OPTIONS.map((option) => (
                  <label
                    className={`flex min-h-[88px] items-start gap-3 rounded-[8px] border p-4 text-sm ${
                      delegationForm.scope.includes(option.value)
                        ? "border-[#34a853] bg-[#eef8f0] text-[#1f6d38]"
                        : "border-slate-200 bg-white text-[#4d5968]"
                    }`}
                    key={option.value}
                  >
                    <input
                      checked={delegationForm.scope.includes(option.value)}
                      className="mt-1"
                      onChange={() => toggleDelegationScope(option.value)}
                      type="checkbox"
                    />
                    <span>
                      <span className="block font-semibold">{option.label}</span>
                      <span className="mt-1 block leading-5 text-[#667386]">
                        {option.description}
                      </span>
                    </span>
                  </label>
                ))}
              </div>
            </div>
            <TextField
              className="md:col-span-2"
              label="Notes"
              onChange={(event) => setDelegationField("notes", event.target.value)}
              value={delegationForm.notes}
            />
            <div className="md:col-span-2 flex justify-end">
              <Button
                disabled={!canSubmitDelegation}
                type="submit"
                variant="contained"
              >
                {isDelegationSubmitting ? "Saving..." : "Link Grower"}
              </Button>
            </div>
          </form>

          <div className="mt-6 divide-y divide-slate-200 rounded-[8px] border border-slate-200">
            {delegations.length === 0 ? (
              <div className="p-4 text-sm">No grower links yet.</div>
            ) : (
              delegations.map((delegation) => (
                <div
                  className="grid gap-4 p-4 md:grid-cols-[minmax(0,1fr)_120px]"
                  key={delegation.public_id}
                >
                  <div>
                    <p className="font-semibold text-[#243245]">
                      {delegation.grower_display_name ||
                        delegation.grower_primary_contact_email ||
                        delegation.grower_profile_public_id}
                    </p>
                    <p className="mt-1 text-sm">
                      {delegation.aggregator_email} - {delegation.status}
                    </p>
                    <p className="mt-1 text-xs uppercase tracking-[0.12em] text-[#75839a]">
                      {(delegation.scope || []).map(formatScopeLabel).join(", ")} - {delegation.link_type}
                    </p>
                  </div>
                  <div className="flex items-center justify-start md:justify-end">
                    <Button
                      color="error"
                      disabled={delegation.status !== "ACTIVE"}
                      onClick={() => revokeDelegation(delegation.public_id)}
                      size="small"
                      variant="outlined"
                    >
                      Revoke
                    </Button>
                  </div>
                </div>
              ))
            )}
          </div>
        </section>
      </div>
    </Page>
  );
}
